import _ from "lodash";
import { defineComponent, computed, ref, onMounted, watch } from "vue";
import { definePropType } from "@types-vue/props";
import { TranslationStatus } from "./index.type";
import LangSelect from "@com/components/ai-translate/lang-select.vue";
import LangTranslate from "@com/components/ai-translate/lang-translate.vue";
export default defineComponent({
    components: {
        LangTranslate: LangTranslate,
        LangSelect: LangSelect
    },
    props: {
        // 支持的语言列表, 当前 PT 平台支持的语言有 83 组
        languageLists: {
            type: definePropType(Array),
            default: function() {
                return [];
            }
        },
        // 当前本地语言，代表默认勾选的原语言选项
        sourceLanguage: String,
        // 当前目标语言，代表默认勾选的目标语言选项，非必填
        targetLanguage: String,
        // 是否启动 AI 翻译，如果没有权限则传 false
        aiTranslatable: {
            type: Boolean,
            default: true
        },
        // 当前项目中已存在的语言数据
        // 新版 PS 中会返回横竖屏两端的数据，只需要在视图中显示一个；并且新增文案时，双端会用同一个文案，需要在每个坑位中单独处理
        projectLanguages: {
            type: definePropType(Object),
            default: function() {}
        },
        // 组件模式，目前有两种模式: create, edit
        mode: {
            type: String,
            default: "create"
        },
        source: {
            type: definePropType(String),
            default: "dm"
        },
        projectId: {
            type: Number,
            default: 0
        },
        disabledSelect: {
            type: Boolean,
            default: false
        },
        // 是否是纯翻译模式，纯翻译模式下，源语言选项允许选择任意语言；非纯翻译模式下，源语言选项只能选择当前项目中已存在的语言
        // 纯翻译模式下，源语言选项没有联动关系
        pureTranslation: {
            type: Boolean,
            default: false
        },
        // 自定义常用语言
        commonLanguages: {
            type: definePropType(Array),
            default: function() {
                return [];
            }
        },
        // 项目来源
        type: {
            type: String,
            default: ""
        }
    },
    setup: function setup(props) {
        // 临时对象，只有在编辑模式下生效
        // 缓存起来在切换语种下的文案，在用户无感知下的临时数据，主要作用于在用户无感知的前提下保存的数据内容，UI 要求的交互设计
        var translatePackageTempData = {};
        // 不支持机翻的语言 code
        var disabledLanguage = [
            "uk",
            "nb",
            "sr",
            "ug"
        ];
        var langTranslate = ref(null);
        var langSelect = ref(null);
        // 如果没有归属的父级，则默认为 __common__
        var COMMON_KEY = "__common__";
        var sourceLanguageModel = ref("");
        var sourceLanguageLists = computed(function() {
            var projectLanguageKeys = Object.keys(props.projectLanguages);
            return props.languageLists.filter(function(item) {
                if (props.pureTranslation) {
                    // 纯翻译模式下，源语言选项允许选择任意语言
                    return true;
                }
                // 返回当前项目中存在的语言
                return projectLanguageKeys.includes(item.code);
            }).map(function(item) {
                return {
                    label: item.name,
                    value: item.code
                };
            });
        });
        var targetLanguageModel = ref("");
        var disabledSelectByEditMode = ref(false);
        var isSelectDisabledLanguage = computed(function() {
            if (disabledLanguage.includes(targetLanguageModel.value) || disabledLanguage.includes(sourceLanguageModel.value)) {
                return true;
            }
            return false;
        });
        var targetLanguageLists = computed(function() {
            var projectLanguageKeys = Object.keys(props.projectLanguages);
            return props.languageLists.filter(function(item) {
                if (props.pureTranslation) {
                    // 纯翻译模式下，源语言选项允许选择任意语言
                    return true;
                }
                if (props.mode === "create") {
                    // 新建项目时，不显示当前项目中已存在的语言
                    return !projectLanguageKeys.includes(item.code);
                }
                // 编辑项目时，显示当前项目中已存在的语言
                return projectLanguageKeys.includes(item.code);
            }).map(function(item) {
                return {
                    label: item.name,
                    value: item.code
                };
            });
        });
        var currentLanguageLists = ref({});
        var setLanguageByProps = function() {
            if (props.sourceLanguage) {
                sourceLanguageModel.value = props.sourceLanguage;
            }
            if (props.targetLanguage) {
                targetLanguageModel.value = props.targetLanguage;
            } else {
                targetLanguageModel.value = "";
            }
        };
        var setLanguageByProjectLanguages = function() {
            var currentLanguagePackage = props.projectLanguages[sourceLanguageModel.value];
            var targetLanguagePackage = props.projectLanguages[targetLanguageModel.value];
            var parentLists = [];
            var tempModel = {};
            if (currentLanguagePackage) {
                // 获取当前语言的父级列表
                currentLanguagePackage.forEach(function(item) {
                    var defaultParent = typeof item.parent === "undefined" || item.parent === "" ? COMMON_KEY : item.parent;
                    var parentValue = typeof item.parentIndex === "number" && item.parentIndex !== -1 ? item.parentIndex : defaultParent;
                    var parentItem = parentLists.find(function(parent) {
                        return String(parent.value) === String(parentValue);
                    });
                    if (!parentItem) {
                        parentLists.push({
                            label: defaultParent,
                            value: String(parentValue)
                        });
                    }
                });
                // 对父级列表进行排序
                // 只有当父级列表中的每一项都是数字时，才进行排序
                if (parentLists.every(function(item) {
                    return !isNaN(Number(item.value)) && Number(item.value) !== -1;
                })) {
                    parentLists.sort(function(a, b) {
                        var aIndex = Number(a.value);
                        var bIndex = Number(b.value);
                        // 如果两者都是 NaN，则按照首字母排序
                        if (isNaN(aIndex) && isNaN(bIndex)) {
                            return a.value.localeCompare(b.value);
                        } else if (isNaN(aIndex)) {
                            return 1;
                        } else if (isNaN(bIndex)) {
                            return -1;
                        }
                        return aIndex - bIndex;
                    });
                }
                parentLists.forEach(function(parent, parentIndex) {
                    // @ts-ignore
                    var children = currentLanguagePackage.filter(function(item) {
                        if (parent.value === COMMON_KEY) {
                            return typeof item.parent === "undefined" || item.parent === "";
                        } else if (typeof item.parentIndex === "number" && item.parentIndex !== -1) {
                            return item.parentIndex === Number(parent.value) && item.parent === parent.label;
                        }
                        return item.parent === parent.label;
                    }).map(function(item) {
                        // 更新模式下，需要把目标语言的文案带过来
                        if (props.mode === "edit" || props.mode === "editCategory") {
                            var sourceLabel = item.value;
                            var targetLabel = "";
                            // 编辑模式下，如果缓存的临时数据有内容，临时数据的优先级更高
                            if (!_.isEmpty(translatePackageTempData) && translatePackageTempData[sourceLanguageModel.value]) {
                                var tempItem = translatePackageTempData[sourceLanguageModel.value].find(function(temp) {
                                    return temp.key === item.key;
                                });
                                sourceLabel = tempItem ? tempItem.value : item.value;
                            }
                            if (targetLanguagePackage) {
                                var targetItem = targetLanguagePackage.find(function(target) {
                                    return target.key === item.key;
                                });
                                if (targetItem) {
                                    // 编辑模式下，如果缓存的临时数据有内容，临时数据的优先级更高
                                    if (!_.isEmpty(translatePackageTempData) && translatePackageTempData[targetLanguageModel.value]) {
                                        var tempItem1 = translatePackageTempData[targetLanguageModel.value].find(function(temp) {
                                            return temp.key === item.key;
                                        });
                                        targetLabel = tempItem1 ? String(tempItem1.value) : String(targetItem.value);
                                    } else {
                                        targetLabel = String(targetItem.value);
                                    }
                                    return {
                                        key: item.key,
                                        checked: false,
                                        label: sourceLabel,
                                        value: targetLabel,
                                        status: TranslationStatus.waiting
                                    };
                                }
                            } else {
                                return {
                                    key: item.key,
                                    checked: false,
                                    label: sourceLabel,
                                    value: "",
                                    status: TranslationStatus.waiting
                                };
                            }
                        }
                        // 新建模式下，需要把目标文案设置为空
                        return {
                            key: item.key,
                            checked: false,
                            label: item.value,
                            value: "",
                            status: TranslationStatus.waiting
                        };
                    }).filter(function(item) {
                        // 过滤掉空的文案
                        return item.label !== "";
                    });
                    tempModel[parentIndex + 1] = {
                        label: parent.label,
                        children: children
                    };
                });
            }
            currentLanguageLists.value = tempModel;
        };
        watch(sourceLanguageModel, function() {
            if (!props.pureTranslation) {
                // 非纯翻译模式下，才会有联动
                setLanguageByProjectLanguages();
            }
        });
        watch(targetLanguageModel, function(val) {
            // 更新模式下，每次切换需要更新目标语言的文案
            if (props.mode === "edit" || props.mode === "editCategory") {
                if (val) {
                    setLanguageByProjectLanguages();
                }
            }
        });
        var resetData = function() {
            setLanguageByProps();
            setLanguageByProjectLanguages();
            disabledSelectByEditMode.value = false;
            setTimeout(function() {
                if (langTranslate.value) {
                    langTranslate.value.clearValidate();
                }
                if (langSelect.value) {
                    langSelect.value.clearValidate();
                }
            });
            translatePackageTempData = {};
        };
        onMounted(function() {
            resetData();
        });
        var validate = function(cb) {
            Promise.all([
                // @ts-ignore
                langTranslate.value.validate(),
                // @ts-ignore
                langSelect.value.validate(), 
            ]).then(function(results) {
                // @ts-ignore
                cb(true, results[0], results[1]);
            }).catch(function(err) {
                // @ts-ignore
                cb(false, err[0], err[1]);
            });
        };
        var getTranslateData = function() {
            var temp = langTranslate.value.getTranslatePackageData();
            if (translatePackageTempData) {
                return Object.assign({}, translatePackageTempData, temp);
            }
            return temp;
        };
        var mouseenter = function() {
            // 编辑模式下，如果没有通过校验，则不允许切换语言
            if (props.mode === "edit" && sourceLanguageModel.value && targetLanguageModel.value) {
                validate(function(valid) {
                    if (!valid) {
                        disabledSelectByEditMode.value = true;
                    } else {
                        var temp = langTranslate.value.getTranslatePackageData();
                        translatePackageTempData = Object.assign({}, translatePackageTempData, temp);
                        disabledSelectByEditMode.value = false;
                    }
                });
            }
        };
        var inputChange = function() {
            if (disabledSelectByEditMode.value) {
                disabledSelectByEditMode.value = false;
            }
        };
        return {
            langTranslate: langTranslate,
            sourceLanguageModel: sourceLanguageModel,
            targetLanguageModel: targetLanguageModel,
            sourceLanguageLists: sourceLanguageLists,
            targetLanguageLists: targetLanguageLists,
            currentLanguageLists: currentLanguageLists,
            validate: validate,
            getTranslateData: getTranslateData,
            resetData: resetData,
            isSelectDisabledLanguage: isSelectDisabledLanguage,
            disabledSelectByEditMode: disabledSelectByEditMode,
            inputChange: inputChange,
            mouseenter: mouseenter,
            langSelect: langSelect
        };
    }
});
